import React, { useState } from 'react';
import { View, Text, Button } from "../../Components/Themed";
import { Modal, TextInput, ScrollView, TouchableOpacity } from 'react-native';
import { useColors } from '../../constants/useColors';
import type { AccountPartnerProps, RouterPartnerProps } from '../types';
import { getRequiredCredentialFields, getCredentialFieldLabels, getCredentialFieldTypes } from '../types/credentials';

type PartnerCredentialsProps = {
    visible: boolean;
    accountPartner: AccountPartnerProps;
    partner: RouterPartnerProps;
    onClose: () => void;
    onUpdate: (partner_id: string, credentials: Record<string, any>) => Promise<void>;
}

const PartnerCredentials = ({ visible, accountPartner, partner, onClose, onUpdate }: PartnerCredentialsProps) => {
    const Colors = useColors();
    const [credentials, setCredentials] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Get credential fields, labels, and types from helper functions
    const credentialFields = getRequiredCredentialFields(partner.name);
    const fieldLabels = getCredentialFieldLabels(partner.name);
    const fieldTypes = getCredentialFieldTypes(partner.name);

    const handleUpdateCredential = (field: string, value: string) => {
        setCredentials(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error when user types
        if (error) setError(null);
    };

    const handleSave = async () => {
        // Validate all required fields are filled
        const missingFields = credentialFields.filter(field => !credentials[field] || credentials[field].trim() === '');
        if (missingFields.length > 0) {
            setError(`Please fill in all required fields: ${missingFields.map(f => fieldLabels[f]).join(', ')}`);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setSuccess(false);
            await onUpdate(accountPartner.partner_id, credentials);
            setSuccess(true);
            // Auto-close after showing success message
            setTimeout(() => {
                onClose();
                setSuccess(false);
                setCredentials({});
            }, 1500);
        } catch (e: any) {
            console.error('Error updating credentials:', e);
            setError(e?.response?.data?.message || e?.message || 'Failed to update credentials. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setCredentials({});
        setError(null);
        setSuccess(false);
        onClose();
    };

    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="fade"
            onRequestClose={onClose}
            statusBarTranslucent={true}
        >
            <View style={{
                flex: 1,
                backgroundColor: 'rgba(0,0,0,0.5)',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <TouchableOpacity
                    activeOpacity={1}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0
                    }}
                    onPress={handleClose}
                />
                <View float style={{
                    width: '90%',
                    maxWidth: 500,
                    maxHeight: '80%',
                    borderRadius: 8,
                    zIndex: 1
                }}>
                    {/* Header */}
                    <View type='header' style={{
                        padding: 15,
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <View transparent>
                            <Text theme='h1'>Update Credentials</Text>
                            <Text theme='description' style={{ marginTop: 3 }}>{partner.name}</Text>
                        </View>
                        <Button
                            type='text'
                            title='Close'
                            onPress={handleClose}
                        />
                    </View>

                    {/* Body */}
                    <ScrollView style={{ maxHeight: 400 }}>
                        <View type='body' style={{ padding: 15 }}>
                            <Text theme='description' style={{ marginBottom: 15 }}>
                                Enter your credentials for {partner.name}. These will be securely encrypted and stored.
                            </Text>

                            <Text theme='description' style={{ marginBottom: 15, fontStyle: 'italic' }}>
                                Note: You are updating new credentials. Any previously saved credentials will be replaced.
                            </Text>

                            {credentialFields.map((field) => {
                                const fieldType = fieldTypes[field];
                                const isMultiline = fieldType === 'textarea';
                                const isSecure = fieldType === 'password';

                                return (
                                    <View key={field} transparent style={{ marginBottom: 15 }}>
                                        <Text style={{ marginBottom: 5, fontWeight: '600' }}>
                                            {fieldLabels[field] || field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ')}
                                        </Text>
                                        <TextInput
                                            style={{
                                                borderWidth: 1,
                                                borderColor: Colors.borders.input,
                                                borderRadius: 4,
                                                padding: 10,
                                                color: Colors.text.input,
                                                backgroundColor: Colors.views.input,
                                                minHeight: isMultiline ? 100 : undefined,
                                                textAlignVertical: isMultiline ? 'top' : 'center'
                                            }}
                                            value={credentials[field] || ''}
                                            onChangeText={(value) => handleUpdateCredential(field, value)}
                                            placeholder={`Enter ${fieldLabels[field] || field}`}
                                            placeholderTextColor={Colors.text.placholder}
                                            secureTextEntry={isSecure}
                                            multiline={isMultiline}
                                            numberOfLines={isMultiline ? 4 : 1}
                                            autoCapitalize='none'
                                            autoCorrect={false}
                                        />
                                    </View>
                                );
                            })}

                            {error && (
                                <View transparent style={{ marginTop: 10, padding: 10, borderRadius: 4, backgroundColor: Colors.buttons.background.error }}>
                                    <Text style={{ color: Colors.text.white }}>{error}</Text>
                                </View>
                            )}

                            {success && (
                                <View transparent style={{ marginTop: 10, padding: 10, borderRadius: 4, backgroundColor: Colors.buttons.background.success }}>
                                    <Text style={{ color: Colors.text.white, fontWeight: '600' }}>✓ Credentials saved successfully!</Text>
                                </View>
                            )}
                        </View>
                    </ScrollView>

                    {/* Footer */}
                    <View type='footer' style={{
                        padding: 15,
                        borderBottomLeftRadius: 8,
                        borderBottomRightRadius: 8,
                        flexDirection: 'row',
                        justifyContent: 'flex-end',
                        gap: 10
                    }}>
                        <Button
                            type='text'
                            title='Cancel'
                            onPress={handleClose}
                            disabled={loading || success}
                        />
                        <Button
                            title={loading ? 'Saving...' : success ? 'Saved!' : 'Save Credentials'}
                            onPress={handleSave}
                            disabled={loading || success}
                            type={success ? 'success' : undefined}
                        />
                    </View>
                </View>
            </View>
        </Modal>
    );
};

export default PartnerCredentials;
